import { BoxOfferPassIcon } from "@/assets/images/svgIcons/boxOffer/passIcon"
import { BoxOfferNoOfferPlanIcon, BoxOfferPlanIcon } from "@/assets/images/svgIcons/boxOffer/planIcon"
import { Text } from "@/components/ui/baseUi/text"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/baseUi/tooltip"
import { KPI_TOOLTIP_DELAY } from "@/modules/boxDashboard/modules/KPI/components/kpiBlock"
import { VariationBadge } from "@/modules/boxDashboard/modules/KPI/components/variationBadge"
import type {
  ActiveOffersKpiModel,
  OfferCountModel,
} from "@/modules/boxDashboard/modules/KPI/modules/offers/models/activeOffersKpiModel"
import { word } from "@modules/core/utils/i18n"

type ActiveOffersKpiBlockProps = {
  model: ActiveOffersKpiModel
}

export function ActiveOffersKpiBlock({ model }: ActiveOffersKpiBlockProps) {
  return (
    <div className="flex flex-col gap-4">
      <Text fontStyle="TM" className="font-semibold text-textOnSurface-default">
        {word("boxDashboard.kpi.activeOffers.title")}
      </Text>

      <div className="flex gap-4">
        <OfferCard
          icon={<BoxOfferPlanIcon size={32} color="currentColor" className="text-textOnSurface-subtle" />}
          title={word("boxDashboard.kpi.activeOffers.membership.title")}
          tooltip={word("boxDashboard.kpi.activeOffers.membership.tooltip")}
          offer={model.membership}
        />
        <OfferCard
          icon={<BoxOfferPassIcon size={32} color="currentColor" className="text-textOnSurface-subtle" />}
          title={word("boxDashboard.kpi.activeOffers.classPass.title")}
          tooltip={word("boxDashboard.kpi.activeOffers.classPass.tooltip")}
          offer={model.classPass}
        />
        <OfferCard
          icon={<BoxOfferNoOfferPlanIcon size={32} color="currentColor" className="text-textOnSurface-subtle" />}
          title={word("boxDashboard.kpi.activeOffers.withoutOffers.title")}
          tooltip={word("boxDashboard.kpi.activeOffers.withoutOffers.tooltip")}
          offer={model.withoutOffers}
        />
      </div>
    </div>
  )
}

type OfferCardProps = {
  icon: React.ReactNode
  title: string
  tooltip: string
  offer: OfferCountModel
}

function OfferCard({ icon, title, tooltip, offer }: OfferCardProps) {
  return (
    <TooltipProvider delay={KPI_TOOLTIP_DELAY}>
      <Tooltip>
        <TooltipTrigger
          className="cursor-help"
          render={
            <div className="flex flex-1 flex-col gap-2 rounded-xl bg-surface-background p-4">
              <div className="flex items-center gap-2">{icon}</div>

              <div className="flex flex-col gap-1">
                <Text fontStyle="LM" className="font-semibold text-textOnSurface-muted">
                  {title}
                </Text>

                <div className="flex items-center gap-2">
                  <Text fontStyle="HM" className="text-textOnSurface-default">
                    {offer.count === 0 && [0, null].includes(offer.comparisonCount) ? "–" : offer.count}
                  </Text>
                  {offer.comparisonCount !== null && offer.variationDirection !== null && (
                    <VariationBadge value={offer.comparisonCount} direction={offer.variationDirection} type="points" />
                  )}
                </div>
              </div>
            </div>
          }
        />
        <TooltipContent className="max-w-80">
          <Text fontStyle="BM" className="wrap-break-word whitespace-break-spaces">
            {tooltip}
          </Text>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
